import { useState, useEffect } from 'react';
import {
  X,
  Users2,
  ShoppingCart,
  Percent,
  Calendar,
  Loader2,
  ShoppingBag,
  BadgePercent,
  PiggyBank,
  Heart,
  Eye,
  Shield,
  Smile
} from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { groupsApi } from '../../services/api';

export default function DealInfoDrawer() {
  const { selectedDeal, showDealInfoDrawer, setShowDealInfoDrawer, confirmJoinGroup, joinedGroupIds, fetchAndSelectGroup } = useAppState();
  const { isGuestMode, setShowGuestPrompt } = useAuth();
  const [groupData, setGroupData] = useState(null);
  const [loadingGroup, setLoadingGroup] = useState(false);

  // Lock body scroll when drawer is open (must be before early return)
  useBodyScrollLock(showDealInfoDrawer && !!selectedDeal);

  // Fetch group data when drawer opens
  useEffect(() => {
    if (showDealInfoDrawer && selectedDeal?.groupId) {
      setLoadingGroup(true);
      groupsApi.getOne(selectedDeal.groupId)
        .then((group) => {
          console.log('Fetched group for drawer:', group);
          setGroupData(group);
        })
        .catch((err) => {
          console.error('Failed to fetch group:', err);
          setGroupData(null);
        })
        .finally(() => setLoadingGroup(false));
    } else {
      setGroupData(null);
    }
  }, [showDealInfoDrawer, selectedDeal?.groupId]);

  if (!showDealInfoDrawer || !selectedDeal) return null;

  // Create a display group object using fetched data or fallback to deal data
  const displayGroup = selectedDeal.groupId ? {
    id: selectedDeal.groupId,
    name: groupData?.name || selectedDeal.groupName || selectedDeal.brand || 'Group',
    members: groupData?.member_count || groupData?.members?.length || 0,
    description: groupData?.description || selectedDeal.terms || '',
  } : null;
  const isAlreadyJoined = joinedGroupIds.includes(selectedDeal.groupId);

  const handleClose = () => {
    setShowDealInfoDrawer(false);
  };

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Drawer - 80% height */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl h-[80vh] flex flex-col animate-slide-up">
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-4 pb-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${selectedDeal.color} rounded-xl flex items-center justify-center text-white text-lg font-bold shadow-md`}>
              {selectedDeal.logo}
            </div>
            <div>
              <h2 className="font-bold text-lg text-gray-900">{selectedDeal.brand}</h2>
              <span className="inline-block bg-emerald-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
                {selectedDeal.offer}
              </span>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50">
          {/* About This Deal */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-bold text-gray-900 mb-3">About This Deal</h3>

            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <Users2 size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">
                  Hosted on: {displayGroup?.name || selectedDeal.brand} group
                </span>
              </div>
              <div className="flex items-start gap-2">
                <ShoppingCart size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">
                  Min Purchase: No Minimum
                </span>
              </div>
              <div className="flex items-start gap-2">
                <Percent size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">
                  Max Cashback: {selectedDeal.offer || '% off'}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <Calendar size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">
                  Expires: Limited Time Only
                </span>
              </div>
            </div>
          </div>

          {/* How it Works */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <h3 className="font-bold text-gray-900 mb-3">How it Works</h3>

            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <ShoppingBag size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">Shop on merchant deals hosted in the group</span>
              </div>
              <div className="flex items-start gap-2">
                <BadgePercent size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">Get cashback rewards on verified card/bank transfer purchases</span>
              </div>
              <div className="flex items-start gap-2">
                <PiggyBank size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">Use your rewards points for future payments to save money</span>
              </div>
            </div>
          </div>

          {/* Group Rules */}
          <div className="bg-white border border-gray-100 rounded-xl p-4">
            <h3 className="font-bold text-gray-900 mb-3">Group Rules</h3>

            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <Heart size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">
                  <strong>Mutual Respect.</strong> Be respectful of other group members
                </span>
              </div>
              <div className="flex items-start gap-2">
                <Eye size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">
                  <strong>Transparency.</strong> Group rewards updates are automatically shared for transparency
                </span>
              </div>
              <div className="flex items-start gap-2">
                <Shield size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">
                  <strong>Security.</strong> Do not share your private info on the group thread
                </span>
              </div>
              <div className="flex items-start gap-2">
                <Smile size={16} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-500">
                  <strong>Enjoy.</strong> Connect with others, celebrate wins, share ideas to help everyone achieve more
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Join CTA - Fixed at bottom */}
        <div className="px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-gray-100">
          <button
            type="button"
            onClick={async () => {
              // Guest users need to sign up to join groups
              if (isGuestMode && !isAlreadyJoined) {
                handleClose();
                setShowGuestPrompt(true);
                return;
              }

              // Join the group if not already joined
              if (!isAlreadyJoined) {
                confirmJoinGroup(selectedDeal);
              }
              handleClose();
              // Fetch group from API and open the group chat thread
              if (selectedDeal.groupId) {
                await fetchAndSelectGroup(selectedDeal.groupId);
              }
            }}
            className="w-full bg-indigo-600 text-white py-4 rounded-xl font-semibold hover:bg-indigo-700 active:scale-[0.98] transition-all"
          >
            {isAlreadyJoined ? 'GO TO GROUP CHAT' : 'JOIN GROUP'}
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
